import { IconActionButton } from "./AdminActions";
import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { api } from "../api";
import { ActionIcon } from "./AdminActions";
import { Modal } from "./Modal";
import { WhatsappIcon } from "./WhatsappIcon";

type WhatsappStatus = {
  status: string;
  qr?: string | null;
  phone?: string | null;
  error?: string | null;
};

export function WhatsappLinkControl({ primaryColor = "#169c98" }: { primaryColor?: string }) {
  const [open, setOpen] = useState(false);
  const [whatsapp, setWhatsapp] = useState<WhatsappStatus>({ status: "disconnected" });
  const [busy, setBusy] = useState(false);
  const errorShown = useRef("");
  const connectionStarted = useRef(false);

  function showError(message: string) {
    const detail = message || "No se pudo generar el código QR de WhatsApp.";
    if (errorShown.current === detail) return;
    errorShown.current = detail;
    void Swal.fire({
      icon: "error",
      title: "No se pudo generar el código QR",
      text: detail,
      confirmButtonText: "Entendido",
      confirmButtonColor: primaryColor,
    });
  }

  useEffect(() => {
    let active = true;
    const loadSummary = () =>
      api<WhatsappStatus>(`/api/integrations/whatsapp/status?_=${Date.now()}`, { cache: "no-store" })
        .then((next) => { if (active) setWhatsapp(next); })
        .catch(() => undefined);
    void loadSummary();
    const timer = window.setInterval(loadSummary, 15_000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    if (!open) {
      connectionStarted.current = false;
      return;
    }
    const load = () =>
      api<WhatsappStatus>(`/api/integrations/whatsapp/status?_=${Date.now()}`, { cache: "no-store" })
        .then((next) => {
          setWhatsapp(next);
          if (next.error) showError(next.error);
          else if (next.status === "ready" || next.status === "disconnected") errorShown.current = "";
          if (next.status === "disconnected" && !connectionStarted.current) {
            connectionStarted.current = true;
            void generateQr();
          }
        })
        .catch((error) => showError(error instanceof Error ? error.message : "No se pudo consultar el estado de WhatsApp"));
    load();
    const timer = window.setInterval(load, 2000);
    return () => window.clearInterval(timer);
  }, [open]);

  async function generateQr() {
    connectionStarted.current = true;
    setBusy(true);
    errorShown.current = "";
    setWhatsapp((current) => ({ ...current, status: "connecting", qr: null, error: null }));
    try {
      const next = await api<WhatsappStatus>("/api/integrations/whatsapp/connect", { method: "POST" });
      setWhatsapp(next);
      if (next.error) showError(next.error);
    } catch (error) {
      const message = error instanceof Error ? error.message : "No se pudo iniciar WhatsApp";
      setWhatsapp({ status: "error", qr: null, phone: null, error: message });
      showError(message);
    } finally {
      setBusy(false);
    }
  }

  async function unlinkWhatsapp() {
    const confirmation = await Swal.fire({
      icon: "warning",
      title: "¿Desenlazar WhatsApp?",
      text: "Organize, Abstracts, meetingPlanner y Workshop dejarán de poder enviar mensajes hasta que se vincule nuevamente.",
      showCancelButton: true,
      confirmButtonText: "Sí, desenlazar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc4c64",
      cancelButtonColor: primaryColor,
      reverseButtons: true,
    });
    if (!confirmation.isConfirmed) return;
    setBusy(true);
    try {
      const next = await api<WhatsappStatus>("/api/integrations/whatsapp/disconnect", { method: "POST" });
      setWhatsapp(next);
      await Swal.fire({ icon: "success", title: "WhatsApp desenlazado", confirmButtonText: "Entendido", confirmButtonColor: primaryColor });
    } catch (error) {
      showError(error instanceof Error ? error.message : "No se pudo desenlazar WhatsApp");
    } finally {
      setBusy(false);
    }
  }

  return <>
    <button type="button" className={`whatsapp-link-trigger${whatsapp.status === "ready" ? " is-linked" : ""}`} title={whatsapp.status === "ready" ? "WhatsApp enlazado" : "Enlazar WhatsApp"} aria-label={whatsapp.status === "ready" ? "WhatsApp enlazado" : "Enlazar WhatsApp"} onClick={() => setOpen(true)}><WhatsappIcon /><span>{whatsapp.status === "ready" ? "ENLAZADO" : "ENLAZAR"}</span></button>
    {open && <Modal title="WhatsApp del evento" eyebrow="Canal de comunicaciones" onClose={() => setOpen(false)}>
      <div className="whatsapp-link-panel">
        {whatsapp.status === "ready" ? <>
          <span className="integration-ready"><WhatsappIcon /></span>
          <h3>Número vinculado</h3>
          <p>+{whatsapp.phone}</p>
          <IconActionButton label="Desenlazar WhatsApp" icon="cancel" tone="secondary" busy={busy} type="button" disabled={busy} onClick={() => void unlinkWhatsapp()} />
        </> : whatsapp.qr ? <>
          <h3>Escaneá el código QR</h3>
          <p>WhatsApp → Dispositivos vinculados → Vincular dispositivo.</p>
          <img src={whatsapp.qr} alt="Código QR para vincular WhatsApp" />
        </> : <>
          <span className="integration-phone"><WhatsappIcon /></span>
          <h3>{whatsapp.status === "authenticated" ? "Validando vinculación…" : whatsapp.status === "connecting" ? "Preparando código QR…" : "Vincular un número celular"}</h3>
          <p>{whatsapp.status === "connecting" || whatsapp.status === "authenticated" ? "Estamos iniciando WhatsApp Web. Esto puede demorar algunos segundos." : "La sesión quedará guardada en este servidor para enviar invitaciones y comunicaciones."}</p>
          {whatsapp.error && <div className="form-error">{whatsapp.error}</div>}
          <button type="button" className="button button-primary" disabled={busy || whatsapp.status === "connecting" || whatsapp.status === "authenticated"} onClick={() => void generateQr()}>
            <ActionIcon name={busy || whatsapp.status === "connecting" || whatsapp.status === "authenticated" ? "refresh" : "message"} />
            {busy || whatsapp.status === "connecting" || whatsapp.status === "authenticated" ? "Preparando código QR…" : "Generar código QR"}
          </button>
        </>}
      </div>
    </Modal>}
  </>;
}
